import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { ArrowLeft } from "lucide-react";
import Button from "../components/ui/Button";
import Footer from "../components/home/Footer";

/* IMAGES */
import air from "../img/products/valves/air-cushion-valve.jpg";
import butterfly from "../img/products/valves/butterfly.jpg";
import control from "../img/products/valves/control.jpg";
import fastners from "../img/products/valves/fastners.jpg";
import fire from "../img/products/valves/fire.webp";
import knife from "../img/products/valves/knife.jpg";
import metal from "../img/products/valves/Metal-Seated-Gate-Valve.png";
import nonreturn from "../img/products/valves/non-return-valve-500x500-500x500.jpg";
import plunger from "../img/products/valves/plunger.avif";
import repair from "../img/products/valves/repair.jpg";
import resilient from "../img/products/valves/Resilient-Seated-Sluice-Valve.jpg";
import sewage from "../img/products/valves/sewage.webp";
import single from "../img/products/valves/single.jpg";
import strap from "../img/products/valves/strap.jpeg";
import tamper from "../img/products/valves/tamper-proof-air-valves.jpeg";
import zero from "../img/products/valves/zero-velocity-valve.jpeg";

/* -----------------------------------
   PRODUCT DATA (16 ITEMS)
----------------------------------- */

const products = [
  {
    name: "Resilient Seated Gate Valve",
    category: "Gate Valve",
    image: resilient,
    description: "Resilient seated gate valve for water distribution networks.",
    sizeRange: "DN50 – DN1200",
    pressure: "PN10 / PN16 / PN20 / PN25",
    body: "Ductile Iron",
    wedge: "Ductile Iron with Encapsulated EPDM",
  },
  {
    name: "Metal Seated Gate Valve",
    category: "Gate Valve",
    image: metal,
    description: "Metal seated gate valve for industrial applications.",
    sizeRange: "DN50 – DN2000",
    pressure: "PN10 / PN16 / PN20 / PN25",
    body: "Cast Iron / Ductile Iron / Cast Steel (WCB)",
    variants: "Rising & Non-Rising Spindle",
  },
  {
    name: "Knife Gate Valve",
    category: "Gate Valve",
    image: knife,
    description: "Knife gate valve for slurry and wastewater handling.",
    sizeRange: "DN50 – DN1600",
    pressure: "PN10 / PN16",
    body: "Cast Iron / Ductile Iron / Cast Steel",
  },
  {
    name: "Butterfly Valve",
    category: "Valve",
    image: butterfly,
    description: "Butterfly valve for isolation and flow regulation.",
    sizeRange: "DN50 – DN3000",
    pressure: "PN10 / PN16 / PN25",
    body: "Cast Iron / Ductile Iron",
  },
  {
    name: "Non-Return Valve",
    category: "Valve",
    image: nonreturn,
    description: "Prevents reverse flow in pipeline systems.",
    sizeRange: "DN50 – DN1400",
    pressure: "PN10 / PN16 / PN25",
    body: "Cast Iron / Ductile Iron",
  },
  {
    name: "Zero Velocity Valve",
    category: "Valve",
    image: zero,
    description: "Prevents water hammer and surge.",
    sizeRange: "DN50 – DN2000",
    pressure: "PN10 / PN16",
    body: "Mild Steel",
    spring: "Spring Steel / SS AISI 304",
  },
  {
    name: "Tamper Proof Air Valve",
    category: "Air Valve",
    image: tamper,
    description: "Automatic air release & vacuum breaker valve.",
    sizeRange: "DN20 – DN200",
    pressure: "PN10 / PN16",
    body: "Ductile Iron",
    trims: "SS AISI 304 / 316",
  },
  {
    name: "Air Cushion Valve",
    category: "Air Valve",
    image: air,
    description: "Controls surge pressure in pipelines.",
    sizeRange: "DN40 – DN400",
    pressure: "PN10 / PN16",
    body: "Mild Steel",
  },
  {
    name: "Plunger / Needle Control Valve",
    category: "Control Valve",
    image: plunger,
    description: "Precision control valve for flow & pressure.",
    sizeRange: "DN50 – DN600",
    pressure: "PN10 / PN16 / PN25",
    body: "Ductile Iron",
  },
  {
    name: "Sewage Air Valve",
    category: "Air Valve",
    image: sewage,
    description: "Air release valve for sewage pipelines.",
    sizeRange: "DN40 – DN200",
    pressure: "PN10 / PN16",
    body: "Ductile Iron",
  },
  {
    name: "Control Valve",
    category: "Valve",
    image: control,
    description: "Flow & pressure regulating control valve.",
    sizeRange: "DN50 – DN1000",
    pressure: "PN10 / PN16 / PN25",
    body: "Ductile Iron",
  },
  {
    name: "Single Faced Sluice Gate / Penstock",
    category: "Sluice Gate",
    image: single,
    description: "Penstock for isolation in water & sewage systems.",
    sizeRange: "200×200 – 2000×2000",
    body: "Cast Iron / Ductile Iron",
  },
  {
    name: "Fire Hydrant Set",
    category: "Fire Fighting",
    image: fire,
    description: "Complete fire hydrant assembly.",
    sizeRange: "DN80",
    pressure: "PN10 / PN16",
    body: "Cast Iron / Ductile Iron",
  },
  {
    name: "Stainless Steel Strap Tapping Saddle",
    category: "Accessories",
    image: strap,
    description: "Tapping saddle for service connections.",
    sizeRange: "DN50 – DN1200",
    pressure: "PN10 / PN16",
    strap: "SS AISI 304 / 316",
  },
  {
    name: "Stainless Steel Repair Clamp",
    category: "Accessories",
    image: repair,
    description: "Quick repair clamp for pipeline leaks.",
    sizeRange: "DN50 – DN800",
    pressure: "PN10 / PN16",
    grip: "EPDM Rubber",
  },
  {
    name: "Fasteners (Bolt & Nut)",
    category: "Accessories",
    image: fastners,
    description: "Fasteners for pipeline and valve assembly.",
    material: "Carbon Steel / High Tensile / Stainless Steel",
  },
];

/* -----------------------------------
   COMPONENT
----------------------------------- */

const ValvesProducts = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* HEADER */}
      <div className="bg-gradient-to-br from-[#0d47a1] via-[#1976d2] to-[#0d47a1] text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Link to={createPageUrl("Home")}>
            <Button variant="ghost" className="text-white hover:text-[#f9d71c] mb-8 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>

          <h1 className="text-4xl md:text-6xl font-light mb-4">
            Industrial <span className="font-semibold">Valves & Accessories</span>
          </h1>

          <p className="text-white/90 text-lg max-w-3xl">
            High-performance valves and accessories for water supply, sewerage and industrial pipelines.
          </p>
        </div>
      </div>

      {/* GRID */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300"
            >

              {/* IMAGE */}
              <div className="h-64 bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain p-4
                             transition-transform duration-300
                             group-hover:scale-105"
                />
              </div>

              {/* CONTENT */}
              <div className="p-6">
                <span className="text-[#1976d2] text-xs uppercase font-medium">
                  {p.category}
                </span>

                <h3 className="text-xl font-semibold text-[#1a237e] mt-2 mb-2">
                  {p.name}
                </h3>

                <p className="text-gray-600 text-sm mb-4">{p.description}</p>

                <div className="space-y-2 text-sm text-gray-600">
                  {p.sizeRange && <p><strong>Size Range:</strong> {p.sizeRange}</p>}
                  {p.pressure && <p><strong>Pressure:</strong> {p.pressure}</p>}
                  {p.body && <p><strong>Body:</strong> {p.body}</p>}
                  {p.wedge && <p><strong>Wedge:</strong> {p.wedge}</p>}
                  {p.variants && <p><strong>Variants:</strong> {p.variants}</p>}
                  {p.trims && <p><strong>Trims:</strong> {p.trims}</p>}
                  {p.spring && <p><strong>Spring:</strong> {p.spring}</p>}
                  {p.strap && <p><strong>Strap:</strong> {p.strap}</p>}
                  {p.grip && <p><strong>Grip:</strong> {p.grip}</p>}
                  {p.material && <p><strong>Material:</strong> {p.material}</p>}
                </div>
              </div>

            </motion.div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ValvesProducts;
